import type { ReactNode } from 'react';
import { Box, Flex, Stack, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import logoHorizontal from '../../assets/auth/logo-horizontal.svg';
import bg from '../../assets/auth/bg.jpg';
import './auth.css';

const tickLabelStyle = {
  fontSize: 11,
  color: 'rgba(255,255,255,0.45)',
  letterSpacing: '0.1em',
} as const;

const tickLineStyle = {
  width: 1,
  height: 48,
  background: 'rgba(255,255,255,0.25)',
} as const;

export function AuthLayout({ children }: { children: ReactNode }) {
  // md = 62em (main.tsx); getInitialValueInEffect: false — чтобы не было мигания панели
  const isDesktop = useMediaQuery('(min-width: 62em)', false, { getInitialValueInEffect: false });

  return (
    <Flex mih="100dvh" direction={isDesktop ? 'row' : 'column'}>
      <Box
        style={{
          position: 'relative',
          overflow: 'hidden',
          ...(isDesktop
            ? { flex: '0 0 54%' }
            : {
                height: 'clamp(240px, 36vh, 340px)',
                flex: '0 0 clamp(240px, 36vh, 340px)',
                borderBottom: '2px solid #0FAC97',
              }),
        }}
      >
        <img
          src={bg}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: isDesktop ? 'center 40%' : 'center 62%',
          }}
        />
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to bottom, rgba(6, 26, 26, 0.90) 0%, rgba(6, 26, 26, 0.35) 42%, rgba(6, 26, 26, 0) 65%)',
          }}
        />

        {/* Декоративные HUD-элементы (только desktop) */}
        {isDesktop && (
          <Box
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
          >
            {/* Колонка рисок сверху */}
            <Box
              style={{
                position: 'absolute',
                left: 24,
                top: 64,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Box key={i} style={{ width: 10, height: 1, background: 'rgba(255,255,255,0.3)' }} />
              ))}
            </Box>

            {/* Вертикальная «линейка» */}
            <Box
              style={{
                position: 'absolute',
                left: 28,
                top: '26%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span style={tickLabelStyle}>01</span>
              <Box style={tickLineStyle} />
              <span style={tickLabelStyle}>02</span>
              <Box style={tickLineStyle} />
              <span style={tickLabelStyle}>03</span>
            </Box>

            {/* Знак «+» */}
            <Box
              style={{
                position: 'absolute',
                left: 28,
                bottom: 180,
                fontSize: 20,
                color: 'rgba(15,172,151,0.8)',
                lineHeight: 1,
              }}
            >
              +
            </Box>

            {/* Координаты */}
            <Box
              style={{
                position: 'absolute',
                left: 28,
                bottom: 64,
                fontSize: 11,
                letterSpacing: '0.08em',
                color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.8,
              }}
            >
              <div>55.7558° N</div>
              <div>37.6176° E</div>
            </Box>
          </Box>
        )}

        <Box
          style={
            isDesktop
              ? { position: 'absolute', top: 88, left: 104, right: 64 }
              : { position: 'absolute', top: 40, left: 24, right: 24 }
          }
        >
          <Text
            fz={12}
            fw={500}
            c="rgba(255,255,255,0.85)"
            style={{ textTransform: 'uppercase', letterSpacing: '0.28em' }}
          >
            ООО «ТК ЕВРАЗИЯ»
          </Text>
          <Box
            style={{
              width: 32,
              height: 2,
              background: '#0FAC97',
              border: 0,
              margin: isDesktop ? '14px 0 36px' : '12px 0 24px',
            }}
          />
          <Title
            order={1}
            c="white"
            fz={isDesktop ? 'clamp(44px, 3.4vw, 58px)' : 32}
            fw={850}
            lh={0.94}
            maw={500}
            style={{
              letterSpacing: '-0.045em',
              textTransform: 'uppercase',
              textShadow: '0 2px 24px rgba(0,0,0,0.45)',
            }}
          >
            <span style={{ display: 'block' }}>Производство.</span>
            <span style={{ display: 'block' }}>Дистрибуция.</span>
            <span style={{ display: 'block' }}>Логистика.</span>
          </Title>
          {isDesktop && (
            <Text c="rgba(255,255,255,0.75)" fz={16} lh={1.55} maw={280} mt={24}>
              Управление активами в сфере производства, дистрибуции и логистики.
            </Text>
          )}
        </Box>
      </Box>

      <Flex
        flex={1}
        align={isDesktop ? 'center' : 'flex-start'}
        justify="center"
        p="xl"
        {...(isDesktop ? {} : { pt: 28 })}
        bg="#F7F5F1"
        style={{ position: 'relative' }}
      >
        <Stack w="100%" maw={460} align="flex-start" gap={0}>
          {isDesktop && (
            <img src={logoHorizontal} alt="EURASIA Tobacco Company" style={{ width: 300 }} />
          )}
          <Text
            fz={11}
            fw={500}
            c="#6B7570"
            mt={isDesktop ? 48 : 0}
            style={{ textTransform: 'uppercase', letterSpacing: '0.28em' }}
          >
            Корпоративный портал
          </Text>
          <Box w="100%" mt={12}>
            {children}
          </Box>
        </Stack>
        {!isDesktop && (
          <img
            src={logoHorizontal}
            alt="EURASIA Tobacco Company"
            style={{ position: 'absolute', right: 16, bottom: 16, width: 150 }}
          />
        )}
      </Flex>
    </Flex>
  );
}
